import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { X, GripVertical, ArrowUp, ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTOR_OPTIONS = [
    { slug: "educational", name: "Educational" },
    { slug: "university", name: "University" },
    { slug: "medical", name: "Medical" },
    { slug: "law-fire", name: "Law Enforcement & Fire" },
    { slug: "office-retail", name: "Office & Retail" },
    { slug: "religious", name: "Religious" },
    { slug: "nonprofit", name: "Nonprofit" },
    { slug: "industrial", name: "Industrial" },
];

const emptyProject = () => ({
    name: "",
    sector: "educational",
    sectorLabel: "Educational",
    partner: "—",
    architect: "—",
    sqft: "—",
    description: "",
    images: [],
});

const ProjectEditor = ({ project, onClose, onSave }) => {
    const [form, setForm] = useState({ ...emptyProject(), ...project });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState("");
    const fileRef = useRef();

    const set = (k, v) => {
        if (k === "sector") {
            const opt = SECTOR_OPTIONS.find((o) => o.slug === v);
            setForm((f) => ({ ...f, sector: v, sectorLabel: opt?.name || v }));
        } else {
            setForm((f) => ({ ...f, [k]: v }));
        }
    };

    const moveImage = (idx, dir) => {
        setForm((f) => {
            const next = [...f.images];
            const to = idx + dir;
            if (to < 0 || to >= next.length) return f;
            [next[idx], next[to]] = [next[to], next[idx]];
            return { ...f, images: next };
        });
    };

    const removeImage = (idx) => {
        setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
    };

    const uploadFiles = async (files) => {
        setErr(""); setUploading(true);
        try {
            const uploads = await Promise.all(
                Array.from(files).map(async (file) => {
                    const fd = new FormData();
                    fd.append("file", file);
                    const { data } = await axios.post(`${API}/admin/projects/upload`, fd);
                    return data.url;
                })
            );
            setForm((f) => ({ ...f, images: [...f.images, ...uploads] }));
        } catch (e) {
            const d = e.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const save = async () => {
        setErr(""); setSaving(true);
        try {
            if (project?.id) {
                await axios.patch(`${API}/admin/projects/${project.id}`, form);
            } else {
                await axios.post(`${API}/admin/projects`, form);
            }
            onSave();
        } catch (e) {
            const d = e.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
            <div className="bg-card border border-border w-full max-w-3xl my-4" data-testid="project-editor">
                <header className="flex items-center justify-between p-6 border-b border-border">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-accent">
                            {project?.id ? "Edit project" : "New project"}
                        </div>
                        <div className="font-display font-black text-2xl uppercase tracking-tight">
                            {form.name || "Untitled"}
                        </div>
                    </div>
                    <button data-testid="editor-close" onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <X className="w-6 h-6" />
                    </button>
                </header>

                <div className="p-6 space-y-5">
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Project name" required>
                            <input data-testid="proj-name" value={form.name} onChange={(e) => set("name", e.target.value)}
                                   className="w-full bg-background border border-border px-3 py-2 focus:border-accent outline-none" />
                        </Field>
                        <Field label="Sector">
                            <select data-testid="proj-sector" value={form.sector} onChange={(e) => set("sector", e.target.value)}
                                    className="w-full bg-background border border-border px-3 py-2 focus:border-accent outline-none">
                                {SECTOR_OPTIONS.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Owner / Partner">
                            <input data-testid="proj-partner" value={form.partner || ""} onChange={(e) => set("partner", e.target.value)}
                                   className="w-full bg-background border border-border px-3 py-2 focus:border-accent outline-none" />
                        </Field>
                        <Field label="Architect">
                            <input data-testid="proj-architect" value={form.architect || ""} onChange={(e) => set("architect", e.target.value)}
                                   className="w-full bg-background border border-border px-3 py-2 focus:border-accent outline-none" />
                        </Field>
                        <Field label="Square footage">
                            <input data-testid="proj-sqft" value={form.sqft || ""} onChange={(e) => set("sqft", e.target.value)}
                                   className="w-full bg-background border border-border px-3 py-2 focus:border-accent outline-none" />
                        </Field>
                    </div>

                    <Field label="Description">
                        <textarea data-testid="proj-description" rows={4} value={form.description || ""}
                                  onChange={(e) => set("description", e.target.value)}
                                  className="w-full bg-background border border-border px-3 py-2 focus:border-accent outline-none" />
                    </Field>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                                Photos ({form.images.length}) — first is cover
                            </label>
                            <input
                                ref={fileRef}
                                data-testid="proj-upload"
                                type="file" accept="image/*" multiple
                                onChange={(e) => e.target.files?.length && uploadFiles(e.target.files)}
                                className="hidden"
                                id="proj-upload-input"
                            />
                            <label htmlFor="proj-upload-input" className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.25em] bg-accent text-accent-foreground px-3 py-2 hover:bg-foreground transition-colors">
                                {uploading ? "Uploading…" : "+ Add photos"}
                            </label>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {form.images.map((src, i) => (
                                <div key={`${src}-${i}`} data-testid={`img-${i}`} className="relative group aspect-square bg-background border border-border overflow-hidden">
                                    <img src={imgSrc(src)} alt="" className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                                        <div className="flex gap-1">
                                            <button onClick={() => moveImage(i, -1)} className="bg-white/90 text-foreground p-1.5 hover:bg-white" title="Move up"><ArrowUp className="w-4 h-4" /></button>
                                            <button onClick={() => moveImage(i, 1)} className="bg-white/90 text-foreground p-1.5 hover:bg-white" title="Move down"><ArrowDown className="w-4 h-4" /></button>
                                            <button onClick={() => removeImage(i)} className="bg-red-600 text-white p-1.5 hover:bg-red-700" title="Remove"><X className="w-4 h-4" /></button>
                                        </div>
                                        {i === 0 && (
                                            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white bg-accent px-2 py-0.5">Cover</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {form.images.length === 0 && (
                            <p className="text-xs text-muted-foreground italic">No photos yet. Upload to add.</p>
                        )}
                    </div>

                    {err && <div className="text-sm text-red-600">{err}</div>}
                </div>

                <footer className="flex items-center justify-end gap-3 p-6 border-t border-border">
                    <button data-testid="editor-cancel" onClick={onClose} className="font-mono text-xs uppercase tracking-[0.25em] border border-border px-4 py-2 hover:border-foreground">Cancel</button>
                    <button data-testid="editor-save" onClick={save} disabled={saving || !form.name}
                            className="font-mono text-xs uppercase tracking-[0.25em] bg-accent text-accent-foreground px-4 py-2 hover:bg-foreground disabled:opacity-60">
                        {saving ? "Saving…" : "Save"}
                    </button>
                </footer>
            </div>
        </div>
    );
};

const Field = ({ label, required, children }) => (
    <div>
        <label className="block font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-1">
            {label}{required && <span className="text-red-600 ml-1">*</span>}
        </label>
        {children}
    </div>
);

const imgSrc = (src) => {
    if (!src) return "";
    if (src.startsWith("http") || src.startsWith("/api/") || src.startsWith("/img/")) {
        return src.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${src.startsWith("/api/") ? src : ""}${src.startsWith("/img/") ? src : ""}` || src : src;
    }
    return src;
};

// Simpler: just build proper URL
const publicImg = (src) => {
    if (!src) return "";
    if (src.startsWith("http")) return src;
    if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
    return src; // /img/... served by frontend
};

const ProjectsAdmin = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/projects`);
            setProjects(data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
        await axios.delete(`${API}/admin/projects/${p.id}`);
        fetchAll();
    };

    const move = async (p, dir) => {
        const sorted = [...projects];
        const idx = sorted.findIndex((x) => x.id === p.id);
        const to = idx + dir;
        if (to < 0 || to >= sorted.length) return;
        const swapWith = sorted[to];
        await Promise.all([
            axios.patch(`${API}/admin/projects/${p.id}`, { order: swapWith.order }),
            axios.patch(`${API}/admin/projects/${swapWith.id}`, { order: p.order }),
        ]);
        fetchAll();
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-accent">Content</div>
                    <div className="font-display font-black text-2xl uppercase tracking-tight">Projects ({projects.length})</div>
                </div>
                <button data-testid="new-project-btn" onClick={() => setCreating(true)}
                        className="font-mono text-[11px] uppercase tracking-[0.25em] bg-accent text-accent-foreground px-4 py-2 hover:bg-foreground">
                    + New project
                </button>
            </div>

            <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-card border-b border-border">
                        <tr className="text-left font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                            <th className="p-3 w-20">Cover</th>
                            <th className="p-3">Name</th>
                            <th className="p-3">Sector</th>
                            <th className="p-3">Owner</th>
                            <th className="p-3 text-center">Photos</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Loading…</td></tr>}
                        {!loading && projects.length === 0 && (
                            <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No projects yet. Click "+ New project".</td></tr>
                        )}
                        {projects.map((p, i) => (
                            <tr key={p.id} data-testid={`proj-row-${p.id}`} className="border-b border-border/60 last:border-b-0">
                                <td className="p-3">
                                    {p.images?.[0] ? (
                                        <img src={publicImg(p.images[0])} alt="" className="w-16 h-16 object-cover bg-background" />
                                    ) : (
                                        <div className="w-16 h-16 bg-background border border-border" />
                                    )}
                                </td>
                                <td className="p-3 font-display font-bold">{p.name}</td>
                                <td className="p-3">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-border text-muted-foreground">
                                        {p.sectorLabel || p.sector}
                                    </span>
                                </td>
                                <td className="p-3 text-muted-foreground">{p.partner || "—"}</td>
                                <td className="p-3 text-center font-mono">{p.images?.length || 0}</td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    <button onClick={() => move(p, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground p-1 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                                    <button onClick={() => move(p, 1)} disabled={i === projects.length - 1} className="text-muted-foreground hover:text-foreground p-1 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                                    <button data-testid={`edit-${p.id}`} onClick={() => setEditing(p)} className="ml-2 font-mono text-xs uppercase tracking-[0.25em] border border-border px-3 py-1 hover:border-accent hover:text-accent">Edit</button>
                                    <button data-testid={`delete-${p.id}`} onClick={() => remove(p)} className="ml-1 text-xs text-muted-foreground hover:text-red-600 px-2 py-1">Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {(editing || creating) && (
                <ProjectEditor
                    project={editing}
                    onClose={() => { setEditing(null); setCreating(false); }}
                    onSave={() => { setEditing(null); setCreating(false); fetchAll(); }}
                />
            )}
        </div>
    );
};

export default ProjectsAdmin;
